import React, { useEffect, useState } from "react";
import { api } from "../api/client";

function fmtDatetime(value) {
  if (!value) return "—";
  const raw = String(value);
  const normalized = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw) ? raw : `${raw.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Jakarta",
  }).format(date);
}

function ActionBadge({ action }) {
  const color =
    action?.startsWith("user.") ? "bg-blue-50 text-blue-700 border-blue-100" :
    action?.startsWith("role.") ? "bg-purple-50 text-purple-700 border-purple-100" :
    action?.startsWith("knowledge.") ? "bg-indigo-50 text-indigo-700 border-indigo-100" :
    action?.startsWith("chat.") ? "bg-green-50 text-green-700 border-green-100" :
    "bg-gray-50 text-gray-600 border-gray-100";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border ${color}`}>
      {action || "—"}
    </span>
  );
}

function EmptyState({ label }) {
  return (
    <div className="py-10 text-center">
      <span className="material-symbols-outlined text-3xl text-gray-200 block mb-2">assignment</span>
      <p className="text-sm text-gray-400">{label}</p>
    </div>
  );
}

function ActionLogCards({ rows }) {
  return (
    <div className="md:hidden divide-y divide-gray-50">
      {rows.map((item) => (
        <div key={item.id} className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <ActionBadge action={item.action} />
            <span className="text-xs text-gray-400 text-right flex-shrink-0">{fmtDatetime(item.created_at)}</span>
          </div>
          <div className="grid gap-2 text-xs">
            <div>
              <p className="font-semibold text-gray-400 uppercase tracking-wide">Entity</p>
              <p className="mt-0.5 text-gray-700 font-mono break-all">{item.entity_type || "-"}</p>
            </div>
            <div>
              <p className="font-semibold text-gray-400 uppercase tracking-wide">Entity ID</p>
              <p className="mt-0.5 text-gray-500 font-mono break-all">{item.entity_id || "-"}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DataAccessCards({ rows }) {
  return (
    <div className="md:hidden divide-y divide-gray-50">
      {rows.map((item) => (
        <div key={item.id} className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-semibold text-gray-800 break-words [overflow-wrap:anywhere]">{item.access_reason || "-"}</p>
            <span className="text-xs text-gray-400 text-right flex-shrink-0">{fmtDatetime(item.created_at)}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-400 uppercase tracking-wide text-xs">Document ID</p>
            <p className="mt-0.5 text-xs text-gray-500 font-mono break-all">{item.document_id || "-"}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AuditView() {
  const [logs, setLogs] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("action");

  useEffect(() => {
    setLoading(true);
    Promise.all([api.auditLogs(), api.dataAccessLogs()])
      .then(([auditRows, accessRows]) => {
        setLogs(Array.isArray(auditRows) ? auditRows : []);
        setAccessLogs(Array.isArray(accessRows) ? accessRows : []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center min-h-16 py-3 pl-14 pr-4 lg:px-6 gap-3">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
            <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>assignment</span>
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-gray-900 text-base md:text-lg leading-tight truncate">Audit Logs</h1>
            <p className="text-xs text-gray-400 hidden sm:block">Riwayat aktivitas dan akses data</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="hidden sm:inline-flex px-2 py-0.5 rounded-md bg-orange-50 text-orange-600 text-[10px] font-bold uppercase tracking-wider border border-orange-100">Compliance</span>
          </div>
        </div>
      </header>

      <div className="flex-1 w-full min-w-0 p-3 sm:p-4 md:p-6">
        <div className="max-w-[1200px] mx-auto min-w-0">
          {error && (
            <div className="mb-5 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-center gap-2">
              <span className="material-symbols-outlined text-[18px]">error</span>
              {error}
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-5 sm:mb-6">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 sm:px-5 py-4 min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Action Logs</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{loading ? "—" : logs.length}</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 sm:px-5 py-4 min-w-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Data Access</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{loading ? "—" : accessLogs.length}</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden min-w-0">
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {[
                { id: "action", label: "Action Logs", icon: "history" },
                { id: "access", label: "Data Access", icon: "visibility" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex flex-1 sm:flex-none items-center justify-center sm:justify-start gap-2 px-3 sm:px-6 py-3.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-[#10a37f] text-[#10a37f] bg-[#10a37f]/5"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="p-10 text-center text-gray-400 text-sm">Memuat data...</div>
            ) : activeTab === "action" ? (
              logs.length === 0 ? (
                <EmptyState label="Belum ada action log." />
              ) : (
                <div>
                  <ActionLogCards rows={logs.slice(0, 50)} />
                  <div className="hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Action</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Entity</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Entity ID</th>
                        <th className="text-right px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Waktu</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {logs.slice(0, 50).map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3.5"><ActionBadge action={item.action} /></td>
                          <td className="px-4 py-3.5 text-gray-600 text-xs font-mono">{item.entity_type || "—"}</td>
                          <td className="px-4 py-3.5 text-gray-400 text-xs font-mono truncate max-w-[180px]">{item.entity_id || "—"}</td>
                          <td className="px-5 py-3.5 text-right text-xs text-gray-400">{fmtDatetime(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {logs.length > 50 && (
                    <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
                      Menampilkan 50 dari {logs.length} log.
                    </div>
                  )}
                </div>
              )
            ) : (
              accessLogs.length === 0 ? (
                <EmptyState label="Belum ada data access log." />
              ) : (
                <div>
                  <DataAccessCards rows={accessLogs.slice(0, 50)} />
                  <div className="hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="text-left px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Alasan Akses</th>
                        <th className="text-left px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Document ID</th>
                        <th className="text-right px-5 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Waktu</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {accessLogs.slice(0, 50).map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="text-gray-800 font-medium">{item.access_reason || "—"}</p>
                          </td>
                          <td className="px-4 py-3.5 text-gray-400 text-xs font-mono truncate max-w-[200px]">{item.document_id || "—"}</td>
                          <td className="px-5 py-3.5 text-right text-xs text-gray-400">{fmtDatetime(item.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {accessLogs.length > 50 && (
                    <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
                      Menampilkan 50 dari {accessLogs.length} log.
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
